<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  currentPage: { type: Number, required: true },
  totalItems: { type: Number, required: true },
  itemsPerPage: { type: Number, default: 25 },
});

const emit = defineEmits(['update:currentPage', 'update:itemsPerPage']);

const { t } = useI18n();

const showItemsPerPageMenu = ref(false);
const itemsPerPageOptions = [
  { value: 10, label: '10 / page' },
  { value: 25, label: '25 / page' },
  { value: 50, label: '50 / page' },
];

const totalPages = computed(() =>
  Math.ceil(props.totalItems / props.itemsPerPage)
);

const startIndex = computed(() => (props.currentPage - 1) * props.itemsPerPage);

const endIndex = computed(() =>
  Math.min(startIndex.value + props.itemsPerPage, props.totalItems)
);

const changePage = newPage => {
  if (newPage >= 1 && newPage <= totalPages.value) {
    emit('update:currentPage', newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const selectItemsPerPage = value => {
  emit('update:itemsPerPage', value);
  showItemsPerPageMenu.value = false;
};
</script>

<template>
  <div
    class="flex justify-between items-center h-14 w-full bg-n-solid-1 border border-n-weak/70 rounded-lg shadow-sm px-4 py-2"
  >
    <!-- Left: Items per page selector -->
    <div class="flex items-center gap-3">
      <span class="text-xs text-n-slate-11 font-medium whitespace-nowrap">
        {{ t('WEBSITE.ORDERS.PAGINATION.ITEMS_PER_PAGE') }}
      </span>
      <div class="relative">
        <button
          class="flex items-center gap-2 h-8 px-3 pr-2.5 text-xs font-medium bg-n-alpha-2 border border-n-weak/60 rounded-lg text-n-slate-12 hover:bg-n-alpha-3 hover:border-n-weak transition-all"
          :class="{
            'bg-n-blue-2 border-n-blue-6': showItemsPerPageMenu,
          }"
          @click="showItemsPerPageMenu = !showItemsPerPageMenu"
        >
          <span class="i-lucide-rows-3 size-3.5 text-n-slate-11" />
          <span class="tabular-nums">{{ itemsPerPage }}</span>
          <span
            class="i-lucide-chevron-down size-3 text-n-slate-11 transition-transform duration-200"
            :class="{ 'rotate-180': showItemsPerPageMenu }"
          />
        </button>

        <!-- Dropdown menu -->
        <div
          v-if="showItemsPerPageMenu"
          v-on-clickaway="() => (showItemsPerPageMenu = false)"
          class="absolute left-0 bottom-full mb-2 w-36 bg-n-solid-1 border border-n-weak/60 shadow-2xl rounded-xl z-[100] py-2.5 overflow-hidden"
        >
          <button
            v-for="option in itemsPerPageOptions"
            :key="option.value"
            class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
            :class="{
              'bg-n-blue-2 text-n-blue-11': itemsPerPage === option.value,
            }"
            @click="selectItemsPerPage(option.value)"
          >
            <span class="font-medium flex items-center gap-2">
              <span
                class="i-lucide-list size-3.5 group-hover:scale-110 transition-transform"
              />
              {{ option.label }}
            </span>
            <span
              v-if="itemsPerPage === option.value"
              class="i-lucide-check size-3.5 text-n-blue-11"
            />
          </button>
        </div>
      </div>
      <span class="text-xs text-n-slate-11 hidden sm:block whitespace-nowrap">
        {{
          t('WEBSITE.ORDERS.PAGINATION.SHOWING', {
            start: startIndex + 1,
            end: endIndex,
            total: totalItems,
          })
        }}
      </span>
    </div>

    <!-- Right: Pagination controls -->
    <div class="flex items-center gap-2">
      <Button
        icon="i-lucide-chevrons-left"
        variant="ghost"
        size="sm"
        color="slate"
        class="!w-8 !h-7"
        :disabled="currentPage === 1"
        @click="changePage(1)"
      />
      <Button
        icon="i-lucide-chevron-left"
        variant="ghost"
        color="slate"
        size="sm"
        class="!w-8 !h-7"
        :disabled="currentPage === 1"
        @click="changePage(currentPage - 1)"
      />
      <div class="inline-flex items-center gap-2 text-xs text-n-slate-11">
        <span
          class="px-3 py-1 bg-n-alpha-2 rounded-md font-medium tabular-nums"
        >
          {{ currentPage }}
        </span>
        <span class="hidden sm:inline">
          {{ t('WEBSITE.ORDERS.PAGINATION.OF') }}
        </span>
        <span class="font-medium tabular-nums">{{ totalPages }}</span>
      </div>
      <Button
        icon="i-lucide-chevron-right"
        variant="ghost"
        color="slate"
        size="sm"
        class="!w-8 !h-7"
        :disabled="currentPage === totalPages"
        @click="changePage(currentPage + 1)"
      />
      <Button
        icon="i-lucide-chevrons-right"
        variant="ghost"
        color="slate"
        size="sm"
        class="!w-8 !h-7"
        :disabled="currentPage === totalPages"
        @click="changePage(totalPages)"
      />
    </div>
  </div>
</template>
